import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Tooltip,
  Alert
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  MoreVert as MoreIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import api from '../../utils/api';

const WebhookLogsViewer = ({ webhookId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalLogs, setTotalLogs] = useState(0);
  const [detailLog, setDetailLog] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/webhooks/${webhookId}/logs`, {
        params: {
          limit: rowsPerPage,
          offset: page * rowsPerPage
        }
      });
      
      if (response.data.success) {
        setLogs(response.data.logs);
        setTotalLogs(response.data.total);
      } else {
        setError(response.data.message || 'Errore nel caricamento dei log');
      }
    } catch (err) {
      console.error('❌ Errore nel caricamento dei log:', err);
      setError(err.response?.data?.message || err.message || 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (webhookId) {
      fetchLogs();
    }
  }, [webhookId, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  const handleViewDetails = (log) => {
    setDetailLog(log);
    setShowDetailDialog(true);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return format(new Date(date), 'dd/MM/yyyy HH:mm:ss', { locale: it });
  };

  const getStatusChip = (statusCode) => {
    if (!statusCode) return <Chip size="small" label="N/A" color="default" />;
    
    if (statusCode >= 200 && statusCode < 300) {
      return <Chip 
        size="small" 
        label={`${statusCode} OK`} 
        color="success" 
        icon={<SuccessIcon />} 
      />;
    } else if (statusCode >= 300 && statusCode < 400) {
      return <Chip 
        size="small" 
        label={`${statusCode} Reindirizzamento`} 
        color="primary" 
      />;
    } else if (statusCode >= 400 && statusCode < 500) {
      return <Chip 
        size="small" 
        label={`${statusCode} Errore Client`} 
        color="warning" 
        icon={<ErrorIcon />} 
      />;
    } else {
      return <Chip 
        size="small" 
        label={`${statusCode} Errore Server`} 
        color="error" 
        icon={<ErrorIcon />} 
      />;
    }
  };

  const formatDuration = (ms) => {
    if (!ms && ms !== 0) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const prettifyJSON = (data) => {
    if (!data) return null;
    
    try {
      // Se è già una stringa, prova a parsarla, altrimenti utilizza l'oggetto
      const jsonObj = typeof data === 'string' ? JSON.parse(data) : data;
      return JSON.stringify(jsonObj, null, 2);
    } catch (e) {
      // Se non è JSON valido, restituisci la stringa originale
      return typeof data === 'string' ? data : JSON.stringify(data);
    }
  };

  if (!webhookId) {
    return (
      <Alert severity="info">
        Seleziona un webhook per visualizzare i log
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="div">
          Log Webhook
        </Typography>
        <IconButton onClick={handleRefresh} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Evento</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Durata</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nessun log disponibile
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{formatDate(log.createdAt)}</TableCell>
                  <TableCell>
                    <Chip label={log.event} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{getStatusChip(log.statusCode)}</TableCell>
                  <TableCell>{formatDuration(log.durationMs)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Dettagli">
                      <IconButton size="small" onClick={() => handleViewDetails(log)}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalLogs}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Righe per pagina:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
      />

      <Dialog 
        open={showDetailDialog} 
        onClose={() => setShowDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Dettagli Log Webhook
        </DialogTitle>
        <DialogContent dividers>
          {detailLog && (
            <Box sx={{ '& > div': { mb: 2 } }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  ID
                </Typography>
                <Typography variant="body2">
                  {detailLog.id}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Data e Ora
                </Typography>
                <Typography variant="body2">
                  {formatDate(detailLog.createdAt)}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Evento
                </Typography>
                <Chip label={detailLog.event} />
              </Box>
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Stato
                </Typography>
                {getStatusChip(detailLog.statusCode)}
                {detailLog.error && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Errore: {detailLog.error}
                  </Typography>
                )}
              </Box>
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Durata
                </Typography>
                <Typography variant="body2">
                  {formatDuration(detailLog.durationMs)}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Payload
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f5f5f5' }}>
                  <pre style={{ margin: 0, overflow: 'auto', fontSize: '0.75rem' }}>
                    {prettifyJSON(detailLog.payload)}
                  </pre>
                </Paper>
              </Box>
              
              {detailLog.response && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Risposta
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f5f5f5' }}>
                    <pre style={{ margin: 0, overflow: 'auto', fontSize: '0.75rem' }}>
                      {prettifyJSON(detailLog.response)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailDialog(false)}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebhookLogsViewer; 